import { useState } from 'react';
import type { ReactElement } from 'react';
import type { DatasetColumn, ReportDetail } from '@tallyframe/core';
import { ApiError } from '../api/errors';
import {
  useCreateReportFromSheet,
  useDescribeSheet,
  useGoogleConnections,
  usePickerToken,
  usePreviewSheet
} from '../api/source-queries';
import type { CsvPreview, SheetDescription } from '../api/types';
import { useSession } from '../app/session-context';
import { ErrorBanner } from '../components/ErrorBanner';
import { ColumnStep } from '../reports/ColumnStep';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { RadioGroupField } from '../ui/RadioGroup';
import { ConnectGoogleButton } from './connect';
import { pickSpreadsheet } from './picker';

const MUTED_TEXT = 'm-0 p-0 font-ui text-sm text-muted';

interface Chosen {
  fileId: string;
  description: SheetDescription;
}

interface Previewed {
  sheetId: number;
  preview: CsvPreview;
}

function defaultGrain(sheetTitle: string, tabTitle: string): string {
  return `One row per row of the tab "${tabTitle}" in the Google Sheet "${sheetTitle}".`;
}

/**
 * New report from a Google Sheet: connect if the caller has no Google
 * connection of their own, pick a spreadsheet, choose a tab, then confirm the
 * columns in the same step the CSV flow uses. The server fetches the sheet
 * itself on create; the browser only ever holds the file id and the user's
 * corrections.
 */
export function SheetCreateDialog(props: {
  onClose(): void;
  onCreated(detail: ReportDetail): void;
}): ReactElement {
  const session = useSession().snapshot;
  const workspaceId = session.workspaceId ?? '';
  const picker = session.config?.googleSheets;
  const connections = useGoogleConnections();
  const pickerToken = usePickerToken();
  const describe = useDescribeSheet();
  const previewSheet = usePreviewSheet();
  const create = useCreateReportFromSheet(workspaceId);

  const [chosen, setChosen] = useState<Chosen | null>(null);
  const [tab, setTab] = useState<string>('');
  const [previewed, setPreviewed] = useState<Previewed | null>(null);
  const [title, setTitle] = useState('');
  const [grain, setGrain] = useState('');
  const [columns, setColumns] = useState<DatasetColumn[]>([]);
  const [busy, setBusy] = useState<false | 'opening' | 'previewing' | 'creating'>(false);
  // True while Google's Picker is up. The modal steps aside meanwhile: its focus
  // trap and outside-hiding would otherwise freeze the Picker's own overlay.
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const returnTo = `/w/${workspaceId}/reports?create=sheet`;
  const hasConnection = connections.data?.connections.some((c) => c.mine && c.status === 'active') ?? false;
  const needsReconnect = ApiError.isApiError(error) && error.code === 'SOURCE_NEEDS_RECONNECT';

  async function openPicker(): Promise<void> {
    if (!picker) return;
    setBusy('opening');
    setError(null);
    try {
      const { accessToken } = await pickerToken.mutateAsync();
      setPicking(true);
      let fileId: string | null;
      try {
        fileId = await pickSpreadsheet({ apiKey: picker.apiKey, appId: picker.appId, accessToken });
      } finally {
        setPicking(false);
      }
      if (fileId === null) return;
      const description = await describe.mutateAsync({ fileId });
      setChosen({ fileId, description });
      setTab(String(description.tabs[0]?.sheetId ?? ''));
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  async function showColumns(): Promise<void> {
    if (!chosen) return;
    setBusy('previewing');
    setError(null);
    try {
      const sheetId = Number(tab);
      const preview = await previewSheet.mutateAsync({ fileId: chosen.fileId, sheetId });
      const tabTitle = chosen.description.tabs.find((t) => t.sheetId === sheetId)?.title ?? '';
      setPreviewed({ sheetId, preview });
      setColumns(preview.columns.map((column) => ({ ...column })));
      setTitle(preview.suggestedName || chosen.description.title);
      setGrain(defaultGrain(chosen.description.title, tabTitle));
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  async function createReport(): Promise<void> {
    // ColumnStep's `disabled` does not reach the type select, so a late edit is stopped here.
    if (!chosen || !previewed || busy) return;
    setBusy('creating');
    setError(null);
    try {
      const detail = await create.mutateAsync({
        fileId: chosen.fileId,
        sheetId: previewed.sheetId,
        title: title.trim(),
        grain: grain.trim(),
        columns
      });
      props.onCreated(detail);
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  const step: 'loading' | 'connect' | 'pick' | 'tab' | 'columns' = previewed
    ? 'columns'
    : chosen
      ? 'tab'
      : connections.isPending
        ? 'loading'
        : hasConnection && !needsReconnect
          ? 'pick'
          : 'connect';

  const failure = error ? (
    <ErrorBanner error={error} actions={needsReconnect ? <ConnectGoogleButton returnTo={returnTo} /> : undefined} />
  ) : null;

  const cancel = (
    <Button onPress={props.onClose} isDisabled={busy === 'creating'}>
      Cancel
    </Button>
  );

  let footer: ReactElement = cancel;
  let body: ReactElement;

  if (step === 'loading') {
    body = <p className={MUTED_TEXT}>Checking your Google connection…</p>;
  } else if (step === 'connect') {
    body = (
      <>
        <p className={MUTED_TEXT}>
          {hasConnection || needsReconnect
            ? 'Your Google connection needs to be renewed before ReportPad can read your sheets.'
            : 'Connect your Google account to build a report from one of your sheets. ReportPad only reads the files you pick.'}
        </p>
        {connections.isError ? <ErrorBanner error={connections.error} /> : failure}
        {needsReconnect ? null : <ConnectGoogleButton returnTo={returnTo} />}
      </>
    );
  } else if (step === 'pick') {
    body = (
      <>
        <p className={MUTED_TEXT}>Pick the spreadsheet to build the report from.</p>
        {picker ? null : <p className={MUTED_TEXT}>Google Sheets is not set up on this server.</p>}
        {failure}
        <div className="m-0 p-0 flex">
          <Button
            variant="primary"
            busy={busy === 'opening'}
            busyLabel="Opening…"
            isDisabled={!picker}
            onPress={() => void openPicker()}
          >
            Choose a spreadsheet
          </Button>
        </div>
      </>
    );
  } else if (step === 'tab' && chosen) {
    body = (
      <>
        <RadioGroupField
          label={`Tab of "${chosen.description.title}"`}
          options={chosen.description.tabs.map((t) => ({
            key: String(t.sheetId),
            label: `${t.title} (${t.rowCount} rows)`
          }))}
          value={tab}
          onChange={setTab}
        />
        {failure}
      </>
    );
    footer = (
      <>
        {cancel}
        <Button
          variant="primary"
          busy={busy === 'previewing'}
          busyLabel="Reading…"
          isDisabled={tab === ''}
          onPress={() => void showColumns()}
        >
          Continue
        </Button>
      </>
    );
  } else {
    body = (
      <>
        <ColumnStep
          columns={columns}
          sampleRows={previewed?.preview.sampleRows ?? []}
          title={title}
          grain={grain}
          onTitle={setTitle}
          onGrain={setGrain}
          onColumn={(index, patch) =>
            setColumns((current) => current.map((column, i) => (i === index ? { ...column, ...patch } : column)))
          }
          disabled={busy === 'creating'}
          columnsHint="Inferred from the sheet. Correct anything that is wrong."
          sampleCaption={previewed ? `First rows of ${previewed.preview.totalRows} in the sheet.` : undefined}
        />
        {failure}
      </>
    );
    footer = (
      <>
        {cancel}
        <Button
          variant="primary"
          busy={busy === 'creating'}
          busyLabel="Creating…"
          isDisabled={title.trim() === '' || grain.trim() === ''}
          onPress={() => void createReport()}
        >
          Create report
        </Button>
      </>
    );
  }

  return (
    <Dialog
      title="New report from a Google Sheet"
      size="lg"
      isOpen={!picking}
      onOpenChange={(open) => {
        // While busy, Google's Picker may be up: it draws its own overlay on
        // document.body, so a click on it reads as a click outside this dialog.
        if (!open && busy === false) props.onClose();
      }}
      footer={footer}
    >
      {body}
    </Dialog>
  );
}
